import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  AddPoolMembersRequest,
  CandidateSearchRow,
  CreatePoolRequest,
  GlobalSearchResult,
  InvitePoolCandidatesRequest,
  InviteResult,
  PoolDetailView,
  PoolMatchView,
  PoolMembershipsActionRequest,
  PoolMergePreview,
  PoolPickerOption,
  PoolRow,
  PoolsAndTagsView,
  ReferralReportRow,
  SearchType,
  TagAdminRow,
  UpdatePoolRequest,
} from '@nest-recruit/shared';
import { apiGet, apiPost, apiPut, request, type ApiError } from '../../../lib/api-client';

/**
 * S25 — POOL-01–04, ADMIN-11, global search and the referral report. Every key sits under
 * `pools`, `pool-match`, `candidate-search` or `candidates`, which are what the server's
 * realtime hints name after a pool change (`POOL_QUERY_KEYS`).
 */

export const POOLS_KEY = ['pools'] as const;
export const POOL_MATCH_KEY = ['pool-match'] as const;
export const CANDIDATE_SEARCH_KEY = ['candidate-search'] as const;

export function usePools(includeArchived = false): UseQueryResult<readonly PoolRow[], ApiError> {
  return useQuery({
    queryKey: [...POOLS_KEY, 'list', includeArchived],
    queryFn: () => apiGet<readonly PoolRow[]>('/pools', { query: includeArchived ? { archived: '1' } : {} }),
    placeholderData: (previous) => previous,
  });
}

export function usePoolOptions(isEnabled = true): UseQueryResult<readonly PoolPickerOption[], ApiError> {
  return useQuery({
    queryKey: [...POOLS_KEY, 'options'],
    queryFn: () => apiGet<readonly PoolPickerOption[]>('/pools/options'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function usePool(id: string): UseQueryResult<PoolDetailView, ApiError> {
  return useQuery({
    queryKey: [...POOLS_KEY, 'detail', id],
    queryFn: () => apiGet<PoolDetailView>(`/pools/${id}`),
    placeholderData: (previous) => previous,
    retry: (count, error) => error.status >= 500 && count < 2,
  });
}

export interface CandidatePools {
  readonly pools: readonly { readonly membershipId: string; readonly poolId: string; readonly name: string; readonly addedAt: string; readonly expiresAt: string }[];
  readonly canEdit: boolean;
}

export function useCandidatePools(candidateId: string, isEnabled: boolean): UseQueryResult<CandidatePools, ApiError> {
  return useQuery({
    queryKey: [...POOLS_KEY, 'candidate', candidateId],
    queryFn: () => apiGet<CandidatePools>(`/pools/candidate/${candidateId}`),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function usePoolMatches(jobId: string, isEnabled = true): UseQueryResult<PoolMatchView, ApiError> {
  return useQuery({
    queryKey: [...POOL_MATCH_KEY, jobId],
    queryFn: () => apiGet<PoolMatchView>(`/jobs/${jobId}/pool-matches`),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function usePoolMatchCount(jobId: string, isEnabled: boolean): UseQueryResult<{ readonly count: number; readonly threshold: number }, ApiError> {
  return useQuery({
    queryKey: [...POOL_MATCH_KEY, jobId, 'count'],
    queryFn: () => apiGet<{ readonly count: number; readonly threshold: number }>(`/jobs/${jobId}/pool-matches/count`),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

/** POOL-04's filters exactly as they sit in the address (06-qol §6). */
export type CandidateSearchParams = Readonly<Record<string, string | number | undefined>>;

export function useCandidateSearch(params: CandidateSearchParams, isEnabled = true): UseQueryResult<{ readonly rows: readonly CandidateSearchRow[]; readonly total: number }, ApiError> {
  return useQuery({
    queryKey: [...CANDIDATE_SEARCH_KEY, params],
    queryFn: async () => {
      const result = await request<readonly CandidateSearchRow[]>('/candidate-search', { query: params });
      return { rows: result.data, total: result.meta?.total ?? result.data.length };
    },
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function useGlobalSearch(query: string, type: 'all' | SearchType, page: number, record: boolean): UseQueryResult<GlobalSearchResult, ApiError> {
  return useQuery({
    queryKey: ['search', query, type, page, record],
    queryFn: ({ signal }) =>
      request<GlobalSearchResult>('/search', { query: { q: query, type, page, pageSize: type === 'all' ? undefined : 25, record: record ? '1' : undefined }, signal }).then((result) => result.data),
    enabled: query.trim().length >= 2,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
  });
}

export function useReferralReport(from: string | undefined, to: string | undefined, isEnabled: boolean): UseQueryResult<readonly ReferralReportRow[], ApiError> {
  return useQuery({
    queryKey: ['referrals', 'report', from ?? '', to ?? ''],
    queryFn: () => apiGet<readonly ReferralReportRow[]>('/referrals/report', { query: { from, to } }),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export function usePoolsAndTags(isEnabled: boolean): UseQueryResult<PoolsAndTagsView, ApiError> {
  return useQuery({
    queryKey: [...POOLS_KEY, 'admin'],
    queryFn: () => apiGet<PoolsAndTagsView>('/admin/pools-and-tags'),
    enabled: isEnabled,
    placeholderData: (previous) => previous,
  });
}

export const poolsApi = {
  create: (input: CreatePoolRequest) => apiPost<PoolRow>('/pools', input),
  update: (id: string, input: UpdatePoolRequest) => apiPut<PoolRow>(`/pools/${id}`, input),
  archive: (id: string, version: number) => apiPost<PoolRow>(`/pools/${id}/archive`, { version }),
  restore: (id: string, version: number) => apiPost<PoolRow>(`/pools/${id}/restore`, { version }),
  addMembers: (id: string, input: AddPoolMembersRequest) => apiPost<{ readonly added: number; readonly alreadyIn: number }>(`/pools/${id}/members`, input),
  removeMembers: (id: string, input: PoolMembershipsActionRequest) => apiPost<{ readonly removed: number }>(`/pools/${id}/members/remove`, input),
  renewConsent: (id: string, input: PoolMembershipsActionRequest) => apiPost<{ readonly renewed: number }>(`/pools/${id}/members/renew`, input),
  invite: (input: InvitePoolCandidatesRequest) => apiPost<InviteResult>('/pools/invite', input),
  runMatch: (jobId: string) => apiPost<PoolMatchView>(`/jobs/${jobId}/pool-matches/run`, {}),
  readCvs: (jobId: string) => apiPost<PoolMatchView>(`/jobs/${jobId}/pool-matches/read`, {}),
  setThreshold: (jobId: string, threshold: number) => apiPut<PoolMatchView>(`/jobs/${jobId}/pool-matches/threshold`, { threshold }),
  // ADMIN-11 (Admin redesign batch 2): a merge carries the version the page showed, and is refused if it changed since.
  mergePool: (id: string, intoId: string, version: number) => apiPost<PoolRow>(`/admin/pools-and-tags/pools/${id}/merge`, { intoId, version }),
  poolMergePreview: (id: string, intoId: string) => apiGet<PoolMergePreview>(`/admin/pools-and-tags/pools/${id}/merge-preview`, { query: { into: intoId } }),
  /** Rename, Retire and Bring back answer with the tag as saved: the page keeps its new version for Undo. */
  renameTag: (id: string, name: string, version: number) => apiPut<TagAdminRow>(`/admin/pools-and-tags/tags/${id}`, { name, version }),
  mergeTag: (id: string, intoId: string, version: number) => apiPost<{ readonly id: string }>(`/admin/pools-and-tags/tags/${id}/merge`, { intoId, version }),
  tagMergePreview: (id: string, intoId: string) => apiGet<PoolMergePreview>(`/admin/pools-and-tags/tags/${id}/merge-preview`, { query: { into: intoId } }),
  retireTag: (id: string, version: number) => apiPost<TagAdminRow>(`/admin/pools-and-tags/tags/${id}/retire`, { version }),
  restoreTag: (id: string, version: number) => apiPost<TagAdminRow>(`/admin/pools-and-tags/tags/${id}/restore`, { version }),
};

/** ADMIN-11's preview of a merge, read again whenever the target changes; never cached long (people move). */
export function useMergePreview(kind: 'pool' | 'tag', fromId: string, intoId: string | null): UseQueryResult<PoolMergePreview, ApiError> {
  return useQuery({
    queryKey: [...POOLS_KEY, 'admin', 'merge-preview', kind, fromId, intoId],
    queryFn: () => (kind === 'pool' ? poolsApi.poolMergePreview(fromId, intoId ?? '') : poolsApi.tagMergePreview(fromId, intoId ?? '')),
    enabled: intoId !== null,
    staleTime: 0,
  });
}
